import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Slider,
    Switch,
    Tooltip,
    Grid,
    Paper
} from '@mui/material';
import { projectService } from '../../services/project.service';
import ColorizeIcon from '@mui/icons-material/Colorize';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import BorderStyleIcon from '@mui/icons-material/BorderStyle';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import SubtitleEditor from './SubtitleEditor';

// Importer les utilitaires de sous-titres
import {
    hexToAssColor,
    assColorToHex,
    assColorToRgba,
    migrateToNewFormat
} from './subtitleUtils';

const SubtitleManager = ({ projectId, onSettingsChange, onSubtitlesChange, currentSettings }) => {
    // Paramètres par défaut au format ASS direct
    const defaultSettings = {
        enabled: true,
        fontName: 'Arial',
        fontSize: 24,
        primaryColor: '&H00FFFFFF', // Blanc en ASS
        backColor: '&H80000000',    // Noir avec 50% de transparence
        alignment: 2,               // Bas-centre
        showBackground: true,
        bold: 0,                    // -1=bold, 0=normal
        italic: 0,                  // -1=italic, 0=normal
        borderStyle: 4,             // 4=box, 1=outline
        outline: 2,
        shadow: 1,
        marginV: 10,
    };

    const [subtitleData, setSubtitleData] = useState({
        subtitles: [],
        settings: {
            ...defaultSettings,
            ...(currentSettings || {})
        }
    });
    const [loading, setLoading] = useState(false);
    const [expandPosition, setExpandPosition] = useState(true);
    const [expandAppearance, setExpandAppearance] = useState(true);
    const [saveTimeout, setSaveTimeout] = useState(null);

    // Constantes de couleur
    const PRIMARY_COLOR = '#6366f1';
    const SECONDARY_COLOR = '#4f46e5';
    const PANEL_BG = '#212224';
    const BACKGROUND_COLOR = '#2D2D2D';
    const ITEM_BG = 'rgba(32, 33, 36, 0.9)';

    // Charger les sous-titres au montage du composant
    useEffect(() => {
        const loadSubtitles = async () => {
            try {
                setLoading(true);
                const result = await projectService.getSubtitles(projectId);
                console.log("Subtitle data loaded:", result);

                if (result) {
                    // Migrer ou utiliser le format ASS directement
                    let processedData;

                    // Vérifier si les données sont déjà au format ASS
                    if (result.settings && typeof result.settings.primaryColor === 'string'
                        && result.settings.primaryColor.startsWith('&H')) {
                        // Données déjà au format ASS
                        processedData = result;
                    } else {
                        // Migrer vers le format ASS
                        processedData = migrateToNewFormat(result);
                        console.log("Data migrated to ASS format:", processedData);
                    }

                    // Fusionner avec les paramètres par défaut
                    processedData.settings = {
                        ...defaultSettings,
                        ...processedData.settings
                    };

                    setSubtitleData(processedData);

                    // Notifier les composants parents
                    if (onSettingsChange) onSettingsChange(processedData.settings);
                    if (onSubtitlesChange) onSubtitlesChange(processedData.subtitles);
                }
            } catch (error) {
                console.error("Error loading subtitles:", error);
            } finally {
                setLoading(false);
            }
        };

        loadSubtitles();
    }, [projectId, onSettingsChange, onSubtitlesChange]);

    // Mettre à jour les sous-titres lorsque des changements sont apportés en externe
    useEffect(() => {
        if (currentSettings) {
            setSubtitleData(prev => ({
                ...prev,
                settings: {
                    ...prev.settings,
                    ...currentSettings
                }
            }));
        }
    }, [currentSettings]);

    // Gérer les changements de paramètres avec sauvegarde automatique
    const handleSettingChange = (setting, value) => {
        const newSettings = { ...subtitleData.settings, [setting]: value };

        // Cas spécial pour showBackground
        if (setting === 'showBackground' && !value) {
            newSettings.borderStyle = 1; // Contour normal sans fond
        } else if (setting === 'showBackground' && value) {
            // Restaurer le style de boîte si le fond est activé
            newSettings.borderStyle = 4; // Boîte avec fond
        }

        setSubtitleData(prev => ({
            ...prev,
            settings: newSettings
        }));

        // Notifier le parent
        if (onSettingsChange) {
            onSettingsChange(newSettings);
        }

        // Annuler le timeout précédent
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Configurer un nouveau timeout pour sauvegarder automatiquement après 3 secondes
        const newTimeout = setTimeout(() => {
            saveChanges({
                settings: newSettings,
                subtitles: subtitleData.subtitles
            });
        }, 3000);

        setSaveTimeout(newTimeout);
    };

    // Sauvegarder les modifications
    const saveChanges = async (dataToSave) => {
        try {
            setLoading(true);
            const result = await projectService.updateSubtitleSettings(
                projectId,
                dataToSave
            );
            console.log("Subtitles saved:", result);
        } catch (error) {
            console.error("Error saving subtitle settings:", error);
        } finally {
            setLoading(false);
        }
    };

    // Gérer les changements de sous-titres depuis le SubtitleEditor
    const handleSubtitlesChange = (newSubtitles) => {
        setSubtitleData(prev => ({
            ...prev,
            subtitles: newSubtitles
        }));

        // Notifier le parent
        if (onSubtitlesChange) {
            onSubtitlesChange(newSubtitles);
        }

        // Annuler le timeout précédent
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Configurer un nouveau timeout pour sauvegarder automatiquement après 3 secondes
        const newTimeout = setTimeout(() => {
            saveChanges({
                settings: subtitleData.settings,
                subtitles: newSubtitles
            });
        }, 3000);

        setSaveTimeout(newTimeout);
    };

    // Gérer le changement de transparence du fond
    const handleBackgroundOpacityChange = (e, newValue) => {
        // Extraire les composantes de la couleur de fond au format ASS
        const backColor = subtitleData.settings.backColor;
        if (backColor && backColor.startsWith('&H')) {
            // Obtenir la couleur sans l'alpha
            const colorWithoutAlpha = backColor.substring(4);
            // Calculer la nouvelle valeur alpha (format ASS: 00=opaque, FF=transparent)
            const alphaHex = Math.round(255 * (1 - newValue)).toString(16).padStart(2, '0').toUpperCase();
            // Reconstruire la couleur avec la nouvelle transparence
            const newBackColor = `&H${alphaHex}${colorWithoutAlpha}`;
            handleSettingChange('backColor', newBackColor);
        }
    };

    // Obtenir l'opacité actuelle du fond
    const getBackgroundOpacity = () => {
        const backColor = subtitleData.settings.backColor;
        if (backColor && backColor.startsWith('&H')) {
            // Extraire la valeur alpha (les 2 premiers caractères après &H)
            const alphaHex = backColor.substring(2, 4);
            // Convertir en valeur alpha (0-1) - dans le format ASS, 00=opaque, FF=transparent
            return 1 - (parseInt(alphaHex, 16) / 255);
        }
        return 0.5; // Valeur par défaut si le format est incorrect
    };

    // Style de panneau pour l'interface
    const panelStyle = {
        backgroundColor: PANEL_BG,
        borderRadius: '4px',
        padding: '0px',
        mb: 1,
    };

    // Style d'en-tête pour les panneaux
    const panelHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
        cursor: 'pointer',
    };

    // Style de contenu pour les panneaux
    const panelContentStyle = {
        py: 1,
        px: 1.5,
    };

    return (
        <Box sx={{
            bgcolor: BACKGROUND_COLOR,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            fontSize: '0.85rem',
            border: '1px solid rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden',
            padding: '8px'
        }}>
            {/* Contenu avec scrollbar */}
            <Box sx={{
                overflowY: 'auto',
                flexGrow: 1,
                p: 1,
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#555',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: '#333',
                    borderRadius: '4px',
                },
            }}>
                {/* Activation/désactivation des sous-titres */}
                <Paper elevation={0} sx={{
                    backgroundColor: PANEL_BG,
                    borderRadius: '4px',
                    padding: '10px',
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography sx={{
                        color: 'white',
                        fontSize: '0.9rem',
                    }}>
                        Activer les sous-titres
                    </Typography>
                    <Switch
                        checked={subtitleData.settings.enabled}
                        onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: PRIMARY_COLOR,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: PRIMARY_COLOR,
                            }
                        }}
                    />
                </Paper>

                {/* Panneau Position */}
                <Paper elevation={0} sx={panelStyle}>
                    <Box
                        sx={{
                            ...panelHeaderStyle,
                            borderBottom: expandPosition ? '1px solid rgba(255, 255, 255, 0.07)' : 'none',
                        }}
                        onClick={() => setExpandPosition(!expandPosition)}
                    >
                        <Typography sx={{
                            color: 'white',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Position
                        </Typography>
                        {expandPosition ?
                            <ExpandLessIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} /> :
                            <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} />
                        }
                    </Box>

                    {expandPosition && (
                        <Box sx={panelContentStyle}>
                            {/* Position verticale et alignement horizontal basés sur la valeur ASS (1-9) */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                        Position et alignement
                                    </Typography>
                                </Box>
                                <Grid container spacing={1}>
                                    {/* Ligne supérieure (7,8,9) */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 7)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 7 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 7 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignLeftIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 8)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 8 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 8 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignCenterIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 9)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 9 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 9 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignRightIcon fontSize="small" />
                                            </Box>
                                        </Box>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'center', mt: 0.5 }}>
                                            Haut
                                        </Typography>
                                    </Grid>

                                    {/* Ligne du milieu (4,5,6) */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 4)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 4 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 4 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignLeftIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 5)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 5 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 5 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignCenterIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 6)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 6 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 6 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignRightIcon fontSize="small" />
                                            </Box>
                                        </Box>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'center', mt: 0.5 }}>
                                            Milieu
                                        </Typography>
                                    </Grid>

                                    {/* Ligne inférieure (1,2,3) */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 1)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 1 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignLeftIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 2)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 2 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 2 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignCenterIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                onClick={() => handleSettingChange('alignment', 3)}
                                                sx={{
                                                    flex: 1,
                                                    height: 28,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    bgcolor: subtitleData.settings.alignment === 3 ? PRIMARY_COLOR : ITEM_BG,
                                                    color: subtitleData.settings.alignment === 3 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                    borderRadius: '4px',
                                                    cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                                    opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                                }}
                                            >
                                                <FormatAlignRightIcon fontSize="small" />
                                            </Box>
                                        </Box>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', textAlign: 'center', mt: 0.5 }}>
                                            Bas
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Marges ASS */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BorderStyleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Marge verticale
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '0.7rem',
                                        bgcolor: ITEM_BG,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '2px'
                                    }}>
                                        {subtitleData.settings.marginV}px
                                    </Typography>
                                </Box>
                                <Slider
                                    size="small"
                                    value={subtitleData.settings.marginV}
                                    onChange={(e, value) => handleSettingChange('marginV', value)}
                                    min={0}
                                    max={100}
                                    disabled={loading || !subtitleData.settings.enabled}
                                    sx={{
                                        color: PRIMARY_COLOR,
                                        height: 3,
                                        p: 0,
                                        '& .MuiSlider-thumb': {
                                            width: 10,
                                            height: 10,
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: `0px 0px 0px 6px ${PRIMARY_COLOR}33`,
                                            }
                                        },
                                        '& .MuiSlider-rail': {
                                            opacity: 0.3,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Panneau Apparence */}
                <Paper elevation={0} sx={panelStyle}>
                    <Box
                        sx={{
                            ...panelHeaderStyle,
                            borderBottom: expandAppearance ? '1px solid rgba(255, 255, 255, 0.07)' : 'none',
                        }}
                        onClick={() => setExpandAppearance(!expandAppearance)}
                    >
                        <Typography sx={{
                            color: 'white',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Apparence
                        </Typography>
                        {expandAppearance ?
                            <ExpandLessIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} /> :
                            <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} />
                        }
                    </Box>

                    {expandAppearance && (
                        <Box sx={panelContentStyle}>
                            {/* Police */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextFormatIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Police
                                        </Typography>
                                    </Box>
                                </Box>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <Select
                                        value={subtitleData.settings.fontName}
                                        onChange={(e) => handleSettingChange('fontName', e.target.value)}
                                        disabled={loading || !subtitleData.settings.enabled}
                                        sx={{
                                            height: '28px',
                                            bgcolor: ITEM_BG,
                                            color: 'white',
                                            fontSize: '0.75rem',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: PRIMARY_COLOR,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: PRIMARY_COLOR,
                                            },
                                        }}
                                    >
                                        <MenuItem value="Arial">Arial</MenuItem>
                                        <MenuItem value="Helvetica">Helvetica</MenuItem>
                                        <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                                        <MenuItem value="Courier New">Courier New</MenuItem>
                                        <MenuItem value="Verdana">Verdana</MenuItem>
                                        <MenuItem value="Georgia">Georgia</MenuItem>
                                        <MenuItem value="Tahoma">Tahoma</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Style de texte (gras, italique) */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <TextFormatIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                    <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                        Style de texte
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Box
                                        onClick={() => !loading && subtitleData.settings.enabled &&
                                            handleSettingChange('bold', subtitleData.settings.bold === -1 ? 0 : -1)}
                                        sx={{
                                            flex: 1,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 0.5,
                                            fontSize: '0.7rem',
                                            bgcolor: subtitleData.settings.bold === -1 ? PRIMARY_COLOR : ITEM_BG,
                                            color: subtitleData.settings.bold === -1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                            borderRadius: '4px',
                                            cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                            opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                        }}
                                    >
                                        <FormatBoldIcon sx={{ fontSize: '1rem' }} />
                                    </Box>
                                    <Box
                                        onClick={() => !loading && subtitleData.settings.enabled &&
                                            handleSettingChange('italic', subtitleData.settings.italic === -1 ? 0 : -1)}
                                        sx={{
                                            flex: 1,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 0.5,
                                            fontSize: '0.7rem',
                                            bgcolor: subtitleData.settings.italic === -1 ? PRIMARY_COLOR : ITEM_BG,
                                            color: subtitleData.settings.italic === -1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                            borderRadius: '4px',
                                            cursor: !loading && subtitleData.settings.enabled ? 'pointer' : 'default',
                                            opacity: !loading && subtitleData.settings.enabled ? 1 : 0.5,
                                        }}
                                    >
                                        <FormatItalicIcon sx={{ fontSize: '1rem' }} />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Taille */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormatSizeIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Taille
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '0.7rem',
                                        bgcolor: ITEM_BG,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '2px'
                                    }}>
                                        {subtitleData.settings.fontSize}px
                                    </Typography>
                                </Box>
                                <Slider
                                    size="small"
                                    value={subtitleData.settings.fontSize}
                                    onChange={(e, value) => handleSettingChange('fontSize', value)}
                                    min={12}
                                    max={48}
                                    disabled={loading || !subtitleData.settings.enabled}
                                    sx={{
                                        color: PRIMARY_COLOR,
                                        height: 3,
                                        p: 0,
                                        '& .MuiSlider-thumb': {
                                            width: 10,
                                            height: 10,
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: `0px 0px 0px 6px ${PRIMARY_COLOR}33`,
                                            }
                                        },
                                        '& .MuiSlider-rail': {
                                            opacity: 0.3,
                                        },
                                    }}
                                />
                            </Box>

                            {/* Couleur de texte */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ColorizeIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Couleur texte
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        fontSize: '0.7rem',
                                        bgcolor: ITEM_BG,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '2px',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    }}>
                                        {assColorToHex(subtitleData.settings.primaryColor)}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: ITEM_BG,
                                    p: 0.5,
                                    borderRadius: '4px',
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                    borderWidth: 1,
                                    borderStyle: 'solid'
                                }}>
                                    <Tooltip title={assColorToHex(subtitleData.settings.primaryColor)}>
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                bgcolor: assColorToRgba(subtitleData.settings.primaryColor),
                                                borderRadius: '2px',
                                                mr: 1,
                                                border: '1px solid rgba(255,255,255,0.2)'
                                            }}
                                        />
                                    </Tooltip>
                                    <input
                                        type="color"
                                        value={assColorToHex(subtitleData.settings.primaryColor)}
                                        onChange={(e) => handleSettingChange('primaryColor', hexToAssColor(e.target.value))}
                                        disabled={loading || !subtitleData.settings.enabled}
                                        style={{
                                            width: '100%', height: 20,
                                            border: 'none',
                                            background: 'none'
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Contour et Ombre */}
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BorderStyleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Contour
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '0.7rem',
                                        bgcolor: ITEM_BG,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '2px'
                                    }}>
                                        {subtitleData.settings.outline}px
                                    </Typography>
                                </Box>
                                <Slider
                                    size="small"
                                    value={subtitleData.settings.outline}
                                    onChange={(e, value) => handleSettingChange('outline', value)}
                                    min={0}
                                    max={4}
                                    step={0.5}
                                    disabled={loading || !subtitleData.settings.enabled}
                                    sx={{
                                        color: PRIMARY_COLOR,
                                        height: 3,
                                        p: 0,
                                        '& .MuiSlider-thumb': {
                                            width: 10,
                                            height: 10,
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: `0px 0px 0px 6px ${PRIMARY_COLOR}33`,
                                            }
                                        },
                                        '& .MuiSlider-rail': {
                                            opacity: 0.3,
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <BlurOnIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Ombre
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '0.7rem',
                                        bgcolor: ITEM_BG,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '2px'
                                    }}>
                                        {subtitleData.settings.shadow}px
                                    </Typography>
                                </Box>
                                <Slider
                                    size="small"
                                    value={subtitleData.settings.shadow}
                                    onChange={(e, value) => handleSettingChange('shadow', value)}
                                    min={0}
                                    max={4}
                                    step={0.5}
                                    disabled={loading || !subtitleData.settings.enabled}
                                    sx={{
                                        color: PRIMARY_COLOR,
                                        height: 3,
                                        p: 0,
                                        '& .MuiSlider-thumb': {
                                            width: 10,
                                            height: 10,
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: `0px 0px 0px 6px ${PRIMARY_COLOR}33`,
                                            }
                                        },
                                        '& .MuiSlider-rail': {
                                            opacity: 0.3,
                                        },
                                    }}
                                />
                            </Box>

                            {/* Arrière-plan */}
                            <Box sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ColorizeIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5, fontSize: '0.9rem' }} />
                                        <Typography sx={{ color: 'white', fontSize: '0.75rem' }}>
                                            Arrière-plan
                                        </Typography>
                                    </Box>
                                    <Switch
                                        size="small"
                                        checked={subtitleData.settings.showBackground}
                                        onChange={(e) => handleSettingChange('showBackground', e.target.checked)}
                                        disabled={loading || !subtitleData.settings.enabled}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: PRIMARY_COLOR,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: PRIMARY_COLOR,
                                            }
                                        }}
                                    />
                                </Box>

                                {subtitleData.settings.showBackground && (
                                    <>
                                        <Grid container spacing={1} sx={{ mb: 0.5 }}>
                                            <Grid item xs={7}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    bgcolor: ITEM_BG,
                                                    p: 0.5,
                                                    borderRadius: '4px',
                                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                                    borderWidth: 1,
                                                    borderStyle: 'solid'
                                                }}>
                                                    <Tooltip title={assColorToHex(subtitleData.settings.backColor)}>
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                bgcolor: assColorToRgba(subtitleData.settings.backColor),
                                                                borderRadius: '2px',
                                                                mr: 1,
                                                                border: '1px solid rgba(255,255,255,0.2)'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                    <input
                                                        type="color"
                                                        value={assColorToHex(subtitleData.settings.backColor)}
                                                        onChange={(e) => {
                                                            // On préserve l'alpha actuel en changeant juste la couleur
                                                            const currentAlpha = getBackgroundOpacity();
                                                            const newBackColor = hexToAssColor(e.target.value, 1 - currentAlpha);
                                                            handleSettingChange('backColor', newBackColor);
                                                        }}
                                                        disabled={loading || !subtitleData.settings.enabled}
                                                        style={{
                                                            width: '100%',
                                                            height: 20,
                                                            border: 'none',
                                                            background: 'none'
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: ITEM_BG,
                                                    height: '100%',
                                                    borderRadius: '4px',
                                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                                    borderWidth: 1,
                                                    borderStyle: 'solid',
                                                    px: 0.5
                                                }}>
                                                    <Typography sx={{
                                                        color: 'rgba(255, 255, 255, 0.7)',
                                                        fontSize: '0.7rem'
                                                    }}>
                                                        Opacité: {Math.round(getBackgroundOpacity() * 100)}%
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Slider
                                            size="small"
                                            value={getBackgroundOpacity()}
                                            onChange={handleBackgroundOpacityChange}
                                            min={0}
                                            max={1}
                                            step={0.05}
                                            disabled={loading || !subtitleData.settings.enabled}
                                            sx={{
                                                color: PRIMARY_COLOR,
                                                height: 3,
                                                p: 0,
                                                '& .MuiSlider-thumb': {
                                                    width: 10,
                                                    height: 10,
                                                    '&:hover, &.Mui-focusVisible': {
                                                        boxShadow: `0px 0px 0px 6px ${PRIMARY_COLOR}33`,
                                                    }
                                                },
                                                '& .MuiSlider-rail': {
                                                    opacity: 0.3,
                                                },
                                            }}
                                        />
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Aperçu */}
                <Paper elevation={0} sx={{
                    ...panelStyle,
                    mb: 2,
                    bgcolor: 'transparent',
                    border: '1px dashed rgba(255, 255, 255, 0.2)',
                    p: 1,
                    display: subtitleData.settings.enabled ? 'block' : 'none'
                }}>
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.7rem',
                        textAlign: 'center',
                        mb: 0.5
                    }}>
                        Aperçu
                    </Typography>

                    <Box sx={{
                        width: '100%',
                        height: 115,
                        bgcolor: '#111',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        borderRadius: '2px',
                        position: 'relative'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            // Positionnement en fonction de l'alignement ASS (1-9)
                            top: subtitleData.settings.alignment >= 7 ? '10px' :
                                (subtitleData.settings.alignment >= 4 ? '50%' : 'auto'),
                            bottom: subtitleData.settings.alignment <= 3 ? '10px' : 'auto',
                            left: [1, 4, 7].includes(subtitleData.settings.alignment) ? '10px' :
                                ([2, 5, 8].includes(subtitleData.settings.alignment) ? '50%' : 'auto'),
                            right: [3, 6, 9].includes(subtitleData.settings.alignment) ? '10px' : 'auto',
                            transform: [2, 5, 8].includes(subtitleData.settings.alignment) ?
                                (subtitleData.settings.alignment === 5 ? 'translate(-50%, -50%)' : 'translateX(-50%)') : 'none',
                            bgcolor: subtitleData.settings.showBackground ? assColorToRgba(subtitleData.settings.backColor) : 'transparent',
                            px: 1,
                            py: 0.5,
                            borderRadius: '2px',
                            maxWidth: '90%',
                            textAlign: [1, 4, 7].includes(subtitleData.settings.alignment) ? 'left' :
                                ([2, 5, 8].includes(subtitleData.settings.alignment) ? 'center' : 'right'),
                            fontWeight: subtitleData.settings.bold === -1 ? 'bold' : 'normal',
                            fontStyle: subtitleData.settings.italic === -1 ? 'italic' : 'normal',
                            textShadow: subtitleData.settings.shadow > 0 ? `0 1px 2px rgba(0,0,0,0.5)` : 'none',
                            boxShadow: subtitleData.settings.showBackground ? '0 0 5px rgba(0,0,0,0.5)' : 'none',
                        }}>
                            <Typography sx={{
                                color: assColorToRgba(subtitleData.settings.primaryColor),
                                fontFamily: subtitleData.settings.fontName,
                                fontSize: Math.min(subtitleData.settings.fontSize * 0.5, 24),
                                lineHeight: 1.2,
                                fontWeight: 'inherit',
                                fontStyle: 'inherit'
                            }}>
                                Exemple de sous-titre
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* SubtitleEditor - passer les données directement */}
                <SubtitleEditor
                    subtitleData={subtitleData}
                    onSubtitlesChange={handleSubtitlesChange}
                    onSettingsChange={onSettingsChange}
                    projectId={projectId}
                />
            </Box>
        </Box>
    );
};

export default SubtitleManager;
